<template>
  <RevStickyBar
    id="sticky-bar-top"
    class="hidden md:flex"
    position="top"
    :visible="showBar"
  >
    <RevContainer :key="`${showBar}`" ref="topBarRef" class="w-full py-1">
      <div class="flex items-center gap-12">
        <template v-if="!isChristmasTime">
          <RevIllustration
            v-if="productImage"
            :alt="productImage.description"
            class="mr-4"
            :height="51"
            sizes="50vw"
            :src="productImage.url"
            :width="51"
          />
          <div class="flex items-center">
            <template v-for="(item, index) in selectedTitle" :key="item.value">
              <div
                v-if="upgradeAccepted && item.isUpgraded"
                class="relative"
                :class="[$style.shimmerText]"
              >
                <span
                  :key="item.value"
                  class="rounded-md inline-block p-4 text-transparent"
                  :class="{
                    [$style.animateShimmer]: upgradeAccepted && item.upgraded,
                  }"
                >
                  {{ item.value }}
                </span>
              </div>

              <span
                v-if="!(upgradeAccepted && item.isUpgraded)"
                class="rounded-md inline-block p-4"
              >
                {{ item.value }}
              </span>
              <span v-if="index !== selectedTitle.length - 1" class="mx-4">
                -
              </span>
            </template>
          </div>
        </template>

        <template v-else>
          <div class="shrink-0">
            <RevIllustration
              v-if="hasDeliveryBeforeChristmas"
              alt=""
              :height="36"
              src="/img/product/delivery-before-christmas.svg"
              :width="36"
            />
            <IconTruckExpress v-else />
          </div>
          <ShippingBlock :shipping="selectedOffer.shipping" />
        </template>

        <div class="flex grow flex-col items-end">
          <Price
            :flash-sales-discount
            :has-original-price="!isWithAfterTradeInPriceLegalChangeEnabled"
            :has-savings="!isWithAfterTradeInPriceLegalChangeEnabled"
            :is-with-after-trade-in-price-legal-change-enabled
            :mobile-plan="selectedMobilePlan"
            :price
            :price-due-today="
              isWithAfterTradeInPriceLegalChangeEnabled
                ? priceWithoutSubsidies
                : null
            "
            :product
            :selected-offer
          />
        </div>
        <div class="ml-20 w-full max-w-256">
          <RevButton
            data-qa="product-page-buy-button-sticky-bar"
            full-width="always"
            variant="primary"
            @click="onAddToCart"
          >
            {{ i18n(translations.addToCart) }}
          </RevButton>
        </div>
      </div>
    </RevContainer>

    <slot name="progress-bar" />
  </RevStickyBar>
</template>

<script lang="ts" setup>
import { computed, useTemplateRef } from 'vue'

import type { Price as PriceType } from '@backmarket/http-api'
import type { MobilePlanOffer } from '@backmarket/http-api/src/api-specs-b2c-services/mobile-plan/types/mobile-plan-offers'
import type { GetPickersResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/pickers'
import type { GetProductResponse } from '@backmarket/http-api/src/api-specs-navigation-experience/product/product'
import { useExperiments } from '@backmarket/nuxt-module-experiments/useExperiments'
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevButton } from '@ds/components/Button'
import { RevContainer } from '@ds/components/Container'
import { RevIllustration } from '@ds/components/Illustration'
import { RevStickyBar } from '@ds/components/StickyBar'
import { IconTruckExpress } from '@ds/icons/IconTruckExpress'

import Price from '~/scopes/product/components/Price/Price.vue'
import ShippingBlock from '~/scopes/product/components/ReassuranceItems/components/ItemShipping/components/ShippingBlock/ShippingBlock.vue'
import { useAddToCart } from '~/scopes/product/composables/useAddToCart'
import { useDeliveryBeforeChristmas } from '~/scopes/product/composables/useDeliveryBeforeChristmas'
import { useForceHideHeader } from '~/scopes/product/composables/useForceHideHeader'
import { useProductTracking } from '~/scopes/product/composables/useProductTracking'

import translations from './StickyBarTop.translations'
import { useShowStickyBarTop } from './composables/useShowStickyBarTop'

const props = defineProps<{
  selectedOffer: NonNullable<GetPickersResponse['selectedOffer']>
  price: PriceType
  product: GetProductResponse
  selectedMobilePlan?: MobilePlanOffer
  tracking: ReturnType<typeof useProductTracking>
  priceWithoutSubsidies?: PriceType | null
  selectedOptions: string[]
  upgrade: GetPickersResponse['upgrade'] | null
  upgradeAccepted: boolean
}>()

const i18n = useI18n()
const { addToCart } = useAddToCart()

const { isChristmasTime, hasDeliveryBeforeChristmas } =
  useDeliveryBeforeChristmas(props.selectedOffer.shipping)

const selectedTitle = computed(() => {
  if (!props.upgrade) {
    return props.selectedOptions.map((option) => ({
      value: option,
      upgraded: false,
      isUpgraded: false,
    }))
  }

  const upgradedFields = Object.values(props.upgrade.pickerFields)

  return props.selectedOptions.map((option, index) => {
    const upgradedField = upgradedFields[index]
    // An item is marked for upgrade only if the text is different
    const willUpgrade = upgradedField.upgrade && option !== upgradedField.value

    return {
      // The text value now changes conditionally within the object
      value: props.upgradeAccepted ? upgradedField.value : option,
      upgraded: upgradedField.upgrade,
      // This new flag is the key to targeting the animation
      isUpgraded: willUpgrade,
    }
  })
})

const productImage = computed(() => {
  return props.product.images[0] ?? null
})

const flashSalesDiscount = computed(
  () =>
    props.product.promoCodes.find((promoCode) => promoCode.isFlashSale)
      ?.discountValue,
)

function onAddToCart() {
  addToCart({
    listingId: props.selectedOffer.offerLegacyId,
    listingPublicId: props.selectedOffer?.offerId,
    tracking: {
      ...props.tracking.product.value,
      list: 'header',
      campaign_id: props.selectedOffer.campaignId,
      population_id: props.selectedOffer.populationId,
    },
    partnerPromoCodes: props.product.includedServiceOffers.partnerPromoCodes,
    isTradeInEligible: props.product.isTradeInEligible,
    promoCodes: props.product.promoCodes,
  })
}

const { showBar } = useShowStickyBarTop()
const topBarRef = useTemplateRef('topBarRef')
useForceHideHeader(topBarRef)

const experiments = useExperiments()
const isWithAfterTradeInPriceLegalChangeEnabled =
  experiments['experiment.ppWithAfterTradeInPriceLegalChange'] ===
  'withAfterTradeInPriceLegalChange'
</script>

<style scoped module>
.shimmerText {
  background-image: linear-gradient(to right, #4055bf 50%, #2f3137 50%);
  background-size: 200% 100%;
  background-position: 100% 0;
  background-clip: text;
  animation: color-change-animation 2s forwards;
}
@keyframes color-change-animation {
  0% {
    background-position: 100% 0;
    @apply text-static-default-hi;
  }
  100% {
    background-position: 0 0;
    @apply text-static-brand-hi;
  }
}
</style>
